import {
  EnumSurveyQuestionType,
  type Maybe,
  type Multilingual,
} from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { isSurveyDesignFieldType } from "./surveyQuestionDesign";

type SurveyQuestionOptionLike = {
  isActive?: Maybe<boolean>;
  label?: Maybe<Multilingual>;
};

type SurveyQuestionLike = {
  type?: Maybe<EnumSurveyQuestionType | string>;
  options?: Maybe<Maybe<SurveyQuestionOptionLike>[]>;
};

export const getActiveSurveyQuestionOptions = (
  question?: Maybe<SurveyQuestionLike>,
) =>
  (question?.options ?? [])
    .filter((option): option is SurveyQuestionOptionLike => Boolean(option))
    .filter(option => option.isActive !== false)
    .map(option => getMultilingualString((option.label ?? {}) as Multilingual))
    .filter(label => label.trim().length > 0);

export const isMultiOptionBooleanQuestion = (
  question?: Maybe<SurveyQuestionLike>,
) =>
  question?.type === EnumSurveyQuestionType.Boolean &&
  getActiveSurveyQuestionOptions(question).length > 0;

export const getDefaultSurveyQuestionResponse = (
  question?: Maybe<SurveyQuestionLike>,
) => {
  if (isSurveyDesignFieldType(question?.type)) {
    return [];
  }

  if (question?.type === EnumSurveyQuestionType.Boolean) {
    return isMultiOptionBooleanQuestion(question) ? [] : ["false"];
  }

  if (question?.type === EnumSurveyQuestionType.Toggle) {
    return ["false"];
  }

  return [""];
};

export const isBooleanResponseAnswered = (
  question: SurveyQuestionLike,
  response?: Maybe<string>[],
) => {
  if (isMultiOptionBooleanQuestion(question)) {
    return (response ?? []).some(value => (value ?? "").trim().length > 0);
  }

  return response?.[0] === "true";
};

export const getBooleanResponseDisplay = (
  question: SurveyQuestionLike,
  response?: Maybe<string>[],
) => {
  if (isMultiOptionBooleanQuestion(question)) {
    return (response ?? []).filter(Boolean).join(", ") || "—";
  }

  return response?.[0] === "true" ? "Yes" : "No";
};
